import React, { useState } from 'react';
import { View, Text, FlatList, StyleSheet, RefreshControl, ActivityIndicator } from 'react-native';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { PAYMENT_COLORS } from '@/constants/residents/mobile/paymentColors';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { useLeaseHistory, type HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import { useAppSelector } from '@/store';
import { selectLeaseIds, selectConnectionLeaseIds } from '@/store/selectors';

interface MobilePaymentHistoryTabProps {
  currentlyDisplayedLeaseId?: number;
}

export const MobilePaymentHistoryTab: React.FC<MobilePaymentHistoryTabProps> = ({
  currentlyDisplayedLeaseId,
}) => {
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userId = authUser?.id;
  
  // Determine which lease to show history for
  const leaseIdToShow = currentlyDisplayedLeaseId || ownLeaseIds[0] || connectionLeaseIds[0];

  const { 
    rows: transactions, 
    isFetching: isLoading, 
    hasError 
  } = useLeaseHistory(leaseIdToShow, userId);
  
  const error = hasError ? 'Failed to load transaction history' : null;

  const [refreshing, setRefreshing] = useState(false);

  const refetch = async () => {
    setRefreshing(true);
    // The hook will automatically refetch when leaseIdToShow changes
    setTimeout(() => setRefreshing(false), 1000);
  };

  const renderTransaction = ({ item }: { item: HistoryRow }) => {
    const isCredit = item.type === 'payment';
    
    return (
      <View style={styles.transactionCard}>
        <View style={styles.transactionHeader}>
          <View style={styles.transactionLeft}>
            <Text style={styles.transactionType}>
              {item.description}
            </Text>
            <Text style={styles.transactionDate}>
              {formatShortDate(item.date)}
            </Text>
            {item.recipient && (
              <Text style={styles.transactionProperty} numberOfLines={1}>
                {item.recipient}
              </Text>
            )}
          </View>
          <View style={styles.transactionRight}>
            <Text style={[
              styles.transactionAmount,
              isCredit && styles.transactionAmountCredit,
            ]}>
              {isCredit ? '-' : '+'}{formatCurrency(Math.abs(item.amount))}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Text style={styles.emptyStateTitle}>No transactions yet</Text>
      <Text style={styles.emptyStateMessage}>
        {currentlyDisplayedLeaseId 
          ? 'Once you make a payment for this lease, it will appear here.'
          : 'Once you make a payment, it will appear here.'}
      </Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorState}>
      <Text style={styles.errorTitle}>Unable to load transactions</Text>
      <Text style={styles.errorMessage}>
        {error || 'Please try again later'}
      </Text>
    </View>
  );

  const renderLoading = () => (
    <View style={styles.loadingState}>
      <ActivityIndicator size="large" color={RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY} />
      <Text style={styles.loadingText}>Loading transaction history...</Text>
    </View>
  );

  if (error) {
    return renderError();
  }

  // Show loading state on initial load (when no transactions yet and still loading)
  if (isLoading && transactions.length === 0 && !refreshing) {
    return renderLoading();
  }

  return (
    <FlatList
      data={transactions}
      renderItem={renderTransaction}
      keyExtractor={(item) => item.id}
      contentContainerStyle={styles.listContainer}
      ListEmptyComponent={!isLoading ? renderEmptyState : null}
      refreshControl={
        <RefreshControl
          refreshing={isLoading}
          onRefresh={refetch}
          tintColor={RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );
};

const styles = StyleSheet.create({
  listContainer: {
    paddingTop: 8,
    paddingBottom: 24,
  },
  transactionCard: {
    backgroundColor: PAYMENT_COLORS.WHITE,
    borderRadius: 12,
    marginHorizontal: 16,
    marginVertical: 4,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  transactionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  transactionLeft: {
    flex: 1,
    marginRight: 12,
  },
  transactionRight: {
    alignItems: 'flex-end',
  },
  transactionType: {
    fontSize: 15,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  transactionDate: {
    fontSize: 13,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    marginBottom: 2,
  },
  transactionProperty: {
    fontSize: 12,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
  },
  transactionAmount: {
    fontSize: 17,
    fontWeight: '700',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  transactionAmountCredit: {
    color: PAYMENT_COLORS.SUCCESS_GREEN,
  },
  transactionStatus: {
    fontSize: 12,
    fontWeight: '500',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingTop: 80,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyStateMessage: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 20,
  },
  errorState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingTop: 80,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: PAYMENT_COLORS.ERROR_RED,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 20,
  },
  loadingState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingTop: 80,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
  },
});

